/**
 * An agent appointment's term, and the state derived from it.
 *
 * ── WHY THIS FILE EXISTS AT ALL ──
 *
 * A mandate had no vocabulary file until now, because it had no vocabulary: it
 * was a row with a start and an end and nothing in between, and
 * `packages/shared/src/api/agents.ts` was enough to describe it.
 * `20261003096000_mandate_terms_and_lifecycle.sql` gives it a term, which is
 * the first thing about an appointment that has a STATE rather than a value —
 * and a state computed in two places drifts.
 *
 * ── THE RULE THIS FILE KEEPS: DERIVED, NEVER STORED ──
 *
 * `covenantState` in `./transfer.ts` settled this shape for the whole schema and
 * the comment beside it is the reason: "What a covenant is doing right now.
 * Derived from `endsAt`, never stored." The same holds twice over here, because
 * the database agrees — `ondo.live_mandate_ids()` evaluates
 * `expires_at > now()` in the statement that asks, so a stored "expired" flag
 * would be a second answer to a question Postgres already answers correctly.
 *
 * SO THE DATE TRAVELS AND THE NUMBER DOES NOT. `ondo.my_agent_mandates()`
 * returns `expires_at` and no day count, and the reason is that a count
 * serialised on the server is wrong by however long the response sat in a
 * cache. It is also what 20260901090000 refused, and the refusal is worth
 * keeping in mind while reading the function that now does it: "inventing '121
 * days' from created_at would put a deadline on screen that nothing enforces."
 * The number below is computed from `expiresAt`, which IS enforced — that is
 * the whole of the difference.
 *
 * `now` IS A PARAMETER on both functions, for `covenantState`'s reason: a test
 * should not have to wait for a term to run out.
 */
/**
 * When both parties are warned, in the order they are sent.
 *
 * MIRRORS THE POSTGRES ENUM `ondo.mandate_warning`, and nothing enforces that —
 * say so in a comment, which is this comment. OND-76 names all three: "Warn both
 * sides one calendar month, one week and one day before mandate expiry."
 *
 * There is no fourth, and no "expired" member. A lapse is not a warning about
 * something that is going to happen.
 */
export const MANDATE_WARNINGS = ["month", "week", "day"];
export function isMandateWarning(value) {
    return (typeof value === "string" && MANDATE_WARNINGS.includes(value));
}
export function mandateTermState(mandate, now = new Date()) {
    if (mandate.expiresAt === null)
        return "open";
    return new Date(mandate.expiresAt).getTime() > now.getTime() ? "running" : "expired";
}
/**
 * How many whole days are left, or null when there is no term.
 *
 * COUNTED IN WHOLE UTC DAYS, AND THAT IS A DECISION RATHER THAN A ROUNDING.
 * The alternative is hours-since-now divided by twenty-four, which makes "1
 * day remaining" mean anything from twenty-five hours to one minute — so a
 * term ending at nine tomorrow morning reads as "1 day" at eight tonight and
 * "0 days" at ten, for a change of two hours. Comparing the DATES instead
 * means the number only ever changes at midnight, which is how a person reads
 * a deadline: "the 14th" is one day away all of the 13th.
 *
 * UTC on both sides, so the answer does not depend on where the reader is
 * sitting. A term is a timestamptz and the warnings are sent off the instant;
 * this number is the human summary of it, and one that jumped when a dealer
 * flew to Basel would be worse than one that is occasionally a few hours out.
 *
 * NEGATIVE FOR A LAPSED TERM rather than clamped at zero, so a caller can tell
 * "ends today" from "ended last week" — `mandateTermState` above is the right
 * test for WHICH side of the line it is on, and a clamped zero would have made
 * the two indistinguishable.
 */
export function mandateDaysRemaining(mandate, now = new Date()) {
    if (mandate.expiresAt === null)
        return null;
    const end = new Date(mandate.expiresAt);
    if (Number.isNaN(end.getTime()))
        return null;
    const endDay = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
    const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    return Math.round((endDay - today) / 86_400_000);
}
/**
 * Whether this appointment carries any agreed terms at all.
 *
 * Used by the screens to decide whether to render a terms block, rather than
 * each one testing six fields and two of them forgetting the seventh. Reads
 * `commissionBp` and `feeMinor` separately because the schema refuses both at
 * once and a reader should not have to know that to ask this question.
 */
export function hasAgreedTerms(mandate) {
    return (mandate.arrangement !== null ||
        mandate.basis !== null ||
        mandate.commissionBp !== null ||
        mandate.feeMinor !== null ||
        mandate.agreedAskingMinor !== null ||
        mandate.agreedFloorMinor !== null);
}
/**
 * A commission as a percentage, from basis points.
 *
 * `20261003096000` decision 11 stores basis points for the reason
 * `./consignment.ts` gives of money — "never a float and never a decimal
 * string" — so 1500 is fifteen per cent and 1525 is fifteen and a quarter.
 * Dividing by a hundred here is the one place that conversion happens.
 */
export function formatCommission(commissionBp) {
    if (commissionBp === null)
        return "";
    const percent = commissionBp / 100;
    return `${Number.isInteger(percent) ? percent : percent.toFixed(2)}%`;
}
//# sourceMappingURL=mandate.js.map